import { tryPostureForPrompt } from '../modules/cashflow.js';
/**
 * ai-insights.js — LLM-Powered Portfolio Insights for SPEKTRX
 *
 * Collects portfolio snapshot data → sends to configured AI → parses structured JSON →
 * renders deep analysis alongside existing rule-based insights on the dashboard.
 *
 * Exports:
 * - requestAIInsights()   — main trigger: collect data → call LLM → render
 * - renderCachedInsights() — render from S.aiInsights cache (on dashboard load)
 * - dismissAIInsight(idx)  — dismiss a single AI insight
 * - clearAIInsights()      — clear all cached insights
 */

import { S, save } from '../core/state.js';
import { chatJSON, isAIConfigured, getAIConfig } from '../core/ai.js';
import { hVal, hPnl, tdAccrued } from '../core/math.js';
import { esc, openTrades, fxUSDTRY, fxEURTRY } from '../core/utils.js';
import { toast } from '../ui/layout.js';
import { rfToTRY } from './cash.js';
import { totalLiabilitiesUSD } from './liabilities.js';
import { monthOutflowTRY, monthKarmaTRY, cashTRYNow } from './cashflow.js';

// ─── CONSTANTS ────────────────────────────────────────────────────

const COOLDOWN_MS = 5 * 60 * 1000; // 5 min between requests
const CACHE_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours cache validity
const MAX_HOLDINGS_IN_PROMPT = 30;
const MAX_TRADES_IN_PROMPT = 15;

let _loading = false;
let _lastRequestTs = 0;

// Severity colors for rendering
const SEV_DOT = { critical: '#FF6B8A', warn: '#FF8C42', info: '#8b5cf6', positive: '#7ff0c4' };

// ─── PORTFOLIO SNAPSHOT ───────────────────────────────────────────

/**
 * Build a concise portfolio snapshot for the LLM prompt.
 * Keeps it compact to minimize token usage while providing enough context.
 */
function _buildSnapshot() {
  // null, not 1. See fxUSDTRY in core/utils.js.
  const fxR = fxUSDTRY();
  const fxE = fxEURTRY();
  // Sum total portfolio value from individual holdings
  const safeH = (S.holdings || []).filter(h => h && !h._del);
  let totalUSD = 0;
  safeH.forEach(h => { try { totalUSD += hVal(h)?.vU || 0; } catch (_) {} });

  // Holdings summary
  const holdings = safeH
    .map(h => {
      try {
        const v = hVal(h);
        const pl = hPnl(h);
        if (!v?.vU && !h.qty) return null;
        return {
          ticker: h.ticker,
          type: h.type,
          subType: h.subType || '',
          currency: h.currency || 'USD',
          qty: h.qty || 0,
          avgCost: +(h.avgCost || 0).toFixed(2),
          // hVal returns { vL, vU } — px does not exist. (audit 2026-08-01)
          currentPrice: +((S.prices && S.prices[h.ticker] && S.prices[h.ticker].price) || 0).toFixed(2),
          valueUSD: +(v?.vU || 0).toFixed(2),
          pnlPct: +(pl?.pnlP || 0).toFixed(2),
          weightPct: totalUSD > 0 ? +(((v?.vU || 0) / totalUSD) * 100).toFixed(1) : 0
        };
      } catch (_e) { return null; }
    })
    .filter(Boolean)
    .sort((a, b) => b.valueUSD - a.valueUSD)
    .slice(0, MAX_HOLDINGS_IN_PROMPT);

  // Open trades — DIRECTIONAL only. Time deposits are not trades: no side, no price, no stop.
  const trades = openTrades(S.trades)
    .map(t => ({
      ticker: t.ticker,
      type: t.type,
      side: t.side || 'long',
      entry: t.entry,
      qty: t.qty,
      leverage: t.leverage || 1,
      sl: t.sl || null,
      tp1: t.tp1 || null,
      tp2: t.tp2 || null,
      currency: t.currency || 'USD',
      status: t.status
    }))
    .slice(0, MAX_TRADES_IN_PROMPT);

  // Cash balances
  const cash = {
    USD: +(S.cash?.USD || 0).toFixed(2),
    // NOW, not the last bank reading — see cashTRYNow(). The prompt must not carry two
    // different TRY balances, this one and the derived one in tryPostureForPrompt().
    TRY: +cashTRYNow().toFixed(2),
    EUR: +(S.cash?.EUR || 0).toFixed(2)
  };

  // Reserve fund
  //
  // ⚠ `rfUnconvertible` — the same flag `ai-chat` grew in plan #3, and this file did not. A leg
  // the app cannot price returns null from `rfToTRY`, `null / fxR` is 0, and the advisor was
  // handed a reserve fund short by that leg as though the shortfall were a fact. `'unavailable'`
  // is the established vocabulary for a number this payload does not have (UI-004).
  let rfUSD = 0, rfUnconvertible = false;
  if (typeof S.reserveFund === 'object') {
    Object.entries(S.reserveFund).forEach(([c, v]) => {
      if (!v) return;
      const tryVal = rfToTRY(v, c);
      if (tryVal == null || !(fxR > 0)) { rfUnconvertible = true; return; }
      rfUSD += tryVal / fxR;
    });
  }

  // Realized P&L summary (last 90 days)
  const now = Date.now();
  const d90 = now - 90 * 86400000;
  const recentRealized = (S.realized || []).filter(r => new Date(r.date).getTime() > d90);
  const realizedPnlUSD = recentRealized.reduce((s, r) => s + (r.pnlUSD || 0), 0);

  // Allocation by type
  const typeAlloc = {};
  holdings.forEach(h => {
    typeAlloc[h.type] = (typeAlloc[h.type] || 0) + h.valueUSD;
  });

  // Currency exposure
  const currencyExp = {};
  holdings.forEach(h => {
    currencyExp[h.currency] = (currencyExp[h.currency] || 0) + h.valueUSD;
  });

  // Monthly cashflow summary (last 3 months)
  const cfKeys = Object.keys(S.cashflow || {}).sort().slice(-3);
  const cashflowSummary = cfKeys.map(k => {
    const mo = S.cashflow[k];
    const income = mo.income || 0;
    // Karma excluded: the mortgage is tracked here but paid by the owner's wife. Sending it
    // as his expense is a fabricated ₺68,000/month burden that the model reasons over and
    // hands back as advice — the same class of defect as a fabricated FX rate in a prompt.
    // (audit 2026-08-04)
    const expense = monthOutflowTRY(mo, true);
    const karma = monthKarmaTRY(mo, true);
    return { month: k, incomeTRY: +income.toFixed(0), expenseTRY: +expense.toFixed(0),
      paidByOthersTRY: +karma.toFixed(0) };
  });

  // Liabilities
  const liabUSD = totalLiabilitiesUSD();

  // TD accrued
  let tdTotalUSD = 0;
  // `tdAccrued` takes the DEPOSIT (`t.td`), not the trade, and returns an OBJECT. Passing the
  // trade made it return null, `null / fxR` is 0, and the prompt carried timeDepositsUSD: $0
  // while ₺611,800 sat in two open deposits. The model then filed a warn card reading
  // "No time deposits despite high Turkish rates — consider parking surplus TRY in a term
  // deposit", which is advice to do the thing he had already done. Every other caller in the
  // app passes `t.td`. (audit 2026-08-08)
  (S.trades || []).filter(t => t.type === 'TimeDeposit' && t.status !== 'Closed').forEach(t => {
    const a = tdAccrued(t.td);
    const acc = a ? (a.currentValue || 0) : 0;
    const cur = t.currency || 'TRY';
    if (cur === 'USD') tdTotalUSD += acc;
    else if (cur === 'EUR') tdTotalUSD += acc * fxE / fxR;
    else tdTotalUSD += fxR > 0 ? acc / fxR : 0;
  });

  return {
    summary: {
      totalPortfolioUSD: +totalUSD.toFixed(2),
      totalCashUSD: (fxR && fxE) ? +(cash.USD + cash.TRY / fxR + cash.EUR * fxE / fxR).toFixed(2) : 'unavailable — no FX rate',
      ...tryPostureForPrompt(),
      reserveFundUSD: rfUnconvertible ? 'unavailable' : +rfUSD.toFixed(2),
      timeDepositsUSD: +tdTotalUSD.toFixed(2),
      liabilitiesUSD: +liabUSD.toFixed(2),
      realizedPnl90dUSD: +realizedPnlUSD.toFixed(2),
      fxRateUSDTRY: fxR,
      holdingsCount: (S.holdings || []).length,
      openTradesCount: trades.length
    },
    holdings,
    typeAllocation: typeAlloc,
    currencyExposure: currencyExp,
    trades,
    cashBalances: cash,
    cashflowSummary
  };
}

/**
 * Generate a simple hash of portfolio state to detect if data changed since last analysis.
 */
// The cache key has to cover anything an insight is built on. Raising the month's portfolio
// commitment moves tryFreeTRY from +40,770 to −40,825 without touching a holding, and the
// cash-drag card would have re-rendered off the superseded figure for up to the 4h TTL with
// no stale marker. (audit 2026-08-08)
function _portfolioHash() {
  const vals = (S.holdings || []).map(h => h.ticker + ':' + (h.qty || 0)).join(',');
  const trades = (S.trades || []).filter(t => t.status !== 'Closed').map(t => t.ticker + ':' + t.qty).join(',');
  let hash = 0;
  const posture = tryPostureForPrompt();
  const str = vals + '|' + trades + '|' + (S.fxRate || 0) + '|' + posture.tryFreeTRY + '|' + posture.tryCommittedTRY;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return String(hash);
}

// ─── LLM PROMPT ───────────────────────────────────────────────────

function _buildMessages(snapshot) {
  const system = `You are a senior portfolio analyst for a personal finance tracker called SPEKTRX.
The user manages a multi-currency portfolio (USD, TRY, EUR) with equities, ETFs, funds, commodities, crypto, and time deposits.
Base currency is USD. The user is based in Turkey${S.settings?.trInflation > 0 ? ` (high inflation environment, ~${(S.settings.trInflation * 100).toFixed(1)}% annual)` : ' (high inflation environment; the exact rate is not recorded — do not state a figure)'}.

Analyze the portfolio data and provide actionable insights. Focus on:
1. ALLOCATION: Over/under-concentration, diversification gaps, rebalancing opportunities
2. RISK: Drawdown exposure, leverage risk, currency mismatch, single-stock concentration
3. PERFORMANCE: Lagging positions, winners to trim, tax-loss harvest candidates
4. OPPORTUNITY: Sectors/types under-represented, cash drag, yield optimization.
   For cash drag use summary.tryFreeTRY, NEVER the raw TRY balance — the difference is money already owed this month. If tryFreeTRY is null, say the commitment is unknown instead of naming a figure.
5. CASH FLOW: Surplus trends, expense-to-income trajectory, savings rate

Return a JSON object with this exact structure:
{
  "insights": [
    {
      "title": "short title (max 60 chars)",
      "body": "actionable explanation (max 200 chars)",
      "category": "Allocation" | "Risk" | "Performance" | "Opportunity" | "Cash Flow",
      "severity": "critical" | "warn" | "info" | "positive",
      "ticker": "optional relevant ticker or null"
    }
  ],
  "summary": "one-sentence portfolio health summary (max 120 chars)"
}

Rules:
- Return 5-10 insights, sorted by severity (critical first)
- Be specific: use actual ticker names, percentages, amounts from the data
- Never give generic advice — every insight must reference the user's actual data
- For TRY-denominated assets, note inflation-adjusted real returns when relevant
- If leverage is present, always flag associated risks
- Do NOT recommend specific stocks to buy — focus on portfolio structure and risk`;

  const user = `Here is my current portfolio snapshot:\n\n${JSON.stringify(snapshot, null, 2)}`;

  return [
    { role: 'system', content: system },
    { role: 'user', content: user }
  ];
}

// ─── CORE FUNCTIONS ───────────────────────────────────────────────

/**
 * Request AI-powered portfolio insights.
 * Collects snapshot → sends to LLM → caches → renders.
 */
export async function requestAIInsights() {
  // Guard: AI configured?
  if (!isAIConfigured()) {
    toast('AI not configured. Go to Settings → AI to set up your provider.', 'a-r');
    return;
  }

  // Guard: cooldown
  const now = Date.now();
  if (_loading) {
    toast('Analysis already in progress...', 'a-y');
    return;
  }
  if (now - _lastRequestTs < COOLDOWN_MS) {
    const remaining = Math.ceil((COOLDOWN_MS - (now - _lastRequestTs)) / 1000);
    toast(`Please wait ${remaining}s before requesting another analysis.`, 'a-y');
    return;
  }

  // Guard: need data
  if (!S.holdings.length) {
    toast('No holdings data to analyze.', 'a-y');
    return;
  }
  if (!S.fxRate || S.fxRate <= 0) {
    toast('FX rate not set — required for analysis.', 'a-r');
    return;
  }

  _loading = true;
  _lastRequestTs = now;
  _renderLoadingState();

  try {
    const snapshot = _buildSnapshot();
    const messages = _buildMessages(snapshot);
    const cfg = getAIConfig();

    const result = await chatJSON(messages, {
      maxTokens: 2000,
      temperature: 0.2,
      signal: AbortSignal.timeout(45000)
    });

    const data = result.data;
    if (!data?.insights || !Array.isArray(data.insights)) {
      throw new Error('Invalid AI response structure');
    }

    // Validate and sanitize insights
    const validSeverities = ['critical', 'warn', 'info', 'positive'];
    const validCategories = ['Allocation', 'Risk', 'Performance', 'Opportunity', 'Cash Flow'];
    const insights = data.insights
      .filter(i => i.title && i.body)
      .map(i => ({
        title: String(i.title).slice(0, 80),
        body: String(i.body).slice(0, 300),
        category: validCategories.includes(i.category) ? i.category : 'Risk',
        severity: validSeverities.includes(i.severity) ? i.severity : 'info',
        ticker: i.ticker ? String(i.ticker) : null,
        dismissed: false
      }))
      .slice(0, 12);

    // Cache in state
    S.aiInsights = {
      items: insights,
      summary: data.summary ? String(data.summary).slice(0, 200) : '',
      generatedAt: new Date().toISOString(),
      model: result.model || cfg.model,
      portfolioHash: _portfolioHash()
    };
    save();

    _renderAIInsights(insights, S.aiInsights);
    toast('AI analysis complete', 'a-g');

  } catch (e) {
    console.error('[AI Insights] Error:', e);
    const msg = e.code === 'CONFIG' ? e.message : 'AI analysis failed: ' + (e.message || 'Unknown error');
    toast(msg, 'a-r');
    _renderErrorState(e.message);
  } finally {
    _loading = false;
  }
}

/**
 * Render cached AI insights on dashboard load (no LLM call).
 */
export function renderCachedInsights() {
  const cached = S.aiInsights;
  if (!cached?.items?.length) {
    _renderEmptyState();
    return;
  }

  // Check cache freshness
  const age = Date.now() - new Date(cached.generatedAt).getTime();
  const stale = age > CACHE_TTL_MS;
  const dataChanged = _portfolioHash() !== cached.portfolioHash;

  const activeItems = cached.items.filter(i => !i.dismissed);
  if (!activeItems.length) {
    _renderEmptyState();
    return;
  }

  _renderAIInsights(activeItems, cached, stale || dataChanged);
}

/**
 * Dismiss a single AI insight by index.
 */
export function dismissAIInsight(idx) {
  const i = parseInt(idx, 10);
  if (!S.aiInsights?.items?.[i]) return;
  S.aiInsights.items[i].dismissed = true;
  save();
  renderCachedInsights();
}

/**
 * Clear all cached AI insights.
 */
export function clearAIInsights() {
  S.aiInsights = { items: [], generatedAt: null, model: '', portfolioHash: '' };
  save();
  _renderEmptyState();
  toast('AI insights cleared', 'a-y');
}

// ─── RENDERING ────────────────────────────────────────────────────

function _getContainer() {
  return document.getElementById('aiDeepList');
}

function _renderLoadingState() {
  const el = _getContainer();
  if (!el) return;
  const parent = document.getElementById('aiDeepCard');
  if (parent) parent.style.display = 'block';

  el.innerHTML = `
    <div style="padding:16px 0;text-align:center">
      <div style="display:inline-block;width:20px;height:20px;border:2px solid var(--bd);border-top-color:var(--acc);border-radius:50%;animation:spin 1s linear infinite"></div>
      <div style="font-size:11px;color:var(--t3);margin-top:8px">Analyzing portfolio with AI...</div>
      <div style="font-size:10px;color:var(--t4);margin-top:4px">This may take 10-30 seconds</div>
    </div>`;
}

function _renderErrorState(msg) {
  const el = _getContainer();
  if (!el) return;
  el.innerHTML = `
    <div style="padding:12px 0;font-size:11px;color:var(--red)">
      ⚠ ${esc(msg || 'Analysis failed')}
      <div style="margin-top:6px">
        <button class="btn-xs" data-action="requestAIInsights" style="font-size:10px;padding:4px 10px">Retry</button>
      </div>
    </div>`;
}

function _renderEmptyState() {
  const el = _getContainer();
  if (!el) return;
  const parent = document.getElementById('aiDeepCard');
  if (parent) parent.style.display = isAIConfigured() ? 'block' : 'none';

  const configured = isAIConfigured();
  el.innerHTML = configured
    ? `<div style="padding:12px 0;font-size:11px;color:var(--t3);text-align:center">
         No AI analysis yet.
         <button class="btn-xs" data-action="requestAIInsights" style="font-size:10px;padding:4px 12px;margin-left:8px">Analyze Now</button>
       </div>`
    : `<div style="padding:12px 0;font-size:11px;color:var(--t3);text-align:center">
         Configure AI in Settings to enable deep portfolio analysis.
       </div>`;
}

function _renderAIInsights(items, meta, stale = false) {
  const el = _getContainer();
  if (!el) return;
  const parent = document.getElementById('aiDeepCard');
  if (parent) parent.style.display = 'block';

  // Update header metadata
  const tsEl = document.getElementById('aiDeepTs');
  if (tsEl && meta?.generatedAt) {
    const dt = new Date(meta.generatedAt);
    tsEl.textContent = dt.toLocaleDateString('en', { month: 'short', day: 'numeric' }) + ' ' +
      dt.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' });
  }
  const modelEl = document.getElementById('aiDeepModel');
  if (modelEl && meta?.model) modelEl.textContent = meta.model;

  const countEl = document.getElementById('aiDeepCount');
  if (countEl) countEl.textContent = items.length;

  // Summary line
  let html = '';
  if (meta?.summary) {
    html += `<div style="padding:8px 12px;margin-bottom:8px;background:rgba(114,38,255,.06);border-radius:6px;font-size:11px;color:var(--t2);line-height:1.4">${esc(meta.summary)}</div>`;
  }

  if (stale) {
    html += `<div style="padding:6px 10px;margin-bottom:8px;background:rgba(255,140,66,.08);border-radius:6px;font-size:10px;color:var(--ora)">
      Portfolio data has changed since this analysis.
      <button class="btn-xs" data-action="requestAIInsights" style="font-size:9px;padding:2px 8px;margin-left:6px">Refresh</button>
    </div>`;
  }

  // Insight items
  const sevOrder = { critical: 0, warn: 1, info: 2, positive: 3 };
  const sorted = [...items].sort((a, b) => (sevOrder[a.severity] || 2) - (sevOrder[b.severity] || 2));

  sorted.forEach((item, idx) => {
    // Find original index in S.aiInsights.items for dismiss
    const origIdx = S.aiInsights?.items?.indexOf(item) ?? idx;
    html += `
    <div class="ai-deep-item" style="display:flex;gap:10px;padding:8px 0;border-bottom:1px solid var(--bd);align-items:flex-start">
      <span style="width:6px;height:6px;border-radius:50%;background:${SEV_DOT[item.severity] || SEV_DOT.info};flex-shrink:0;margin-top:5px"></span>
      <div style="flex:1;min-width:0">
        <div style="display:flex;justify-content:space-between;align-items:center;gap:6px">
          <strong style="font-size:12px;color:var(--t1)">${esc(item.title)}</strong>
          <div style="display:flex;align-items:center;gap:4px;flex-shrink:0">
            <span style="font-size:9px;padding:1px 6px;background:rgba(114,38,255,.08);border-radius:4px;color:var(--acc3)">${esc(item.category)}</span>
            ${item.ticker ? `<span class="gls-soft" style="font-size:9px;padding:1px 5px;border-radius:3px;color:var(--t3);font-family:monospace">${esc(item.ticker)}</span>` : ''}
          </div>
        </div>
        <div style="font-size:11px;color:var(--t2);line-height:1.4;margin-top:2px">${esc(item.body)}</div>
      </div>
      <button style="background:none;border:none;cursor:pointer;padding:2px;color:var(--t4);font-size:12px;flex-shrink:0;opacity:.5" data-action="dismissAIInsight" data-args='["${origIdx}"]' title="Dismiss">×</button>
    </div>`;
  });

  el.innerHTML = html;
}

// CSS keyframes for spinner (injected once)
if (typeof document !== 'undefined' && !document.getElementById('ai-spin-style')) {
  const style = document.createElement('style');
  style.id = 'ai-spin-style';
  style.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
  document.head.appendChild(style);
}
